import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

type FormTier = {
	uiId: string;
	name: string;
	color: string;
	min: number;
	max: number;
	noAlert: boolean;
	deadlineDays: number;
	deadlineHours: number;
	deadlineMinutes: number;
	autoHibernate: boolean;
};

type HistoryVersion = {
	id: string;
	version: string;
	createdAt: string;
	createdBy: string;
	tierCount: number;
	tiers: FormTier[];
};

const DEFAULT_TIERS: FormTier[] = [
	{
		uiId: 'tier_low',
		name: 'Low',
		color: '#22c55e',
		min: 0,
		max: 40,
		noAlert: true,
		deadlineDays: 0,
		deadlineHours: 0,
		deadlineMinutes: 0,
		autoHibernate: false,
	},
	{
		uiId: 'tier_medium',
		name: 'Medium',
		color: '#f59e0b',
		min: 40,
		max: 70,
		noAlert: false,
		deadlineDays: 5,
		deadlineHours: 0,
		deadlineMinutes: 0,
		autoHibernate: false,
	},
	{
		uiId: 'tier_high',
		name: 'High',
		color: '#ef4444',
		min: 70,
		max: 100,
		noAlert: false,
		deadlineDays: 2,
		deadlineHours: 0,
		deadlineMinutes: 0,
		autoHibernate: true,
	},
];

const MOCK_HISTORY: HistoryVersion[] = [
	{
		id: 'v3',
		version: 'v3',
		createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
		createdBy: 'Alex Morgan',
		tierCount: 3,
		tiers: DEFAULT_TIERS,
	},
	{
		id: 'v2',
		version: 'v2',
		createdAt: new Date(Date.now() - 86400000 * 14).toISOString(),
		createdBy: 'Jordan Kim',
		tierCount: 3,
		tiers: [
			{
				uiId: 'v2_low',
				name: 'Low',
				color: '#22c55e',
				min: 0,
				max: 50,
				noAlert: true,
				deadlineDays: 0,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: false,
			},
			{
				uiId: 'v2_medium',
				name: 'Medium',
				color: '#f59e0b',
				min: 50,
				max: 80,
				noAlert: false,
				deadlineDays: 7,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: false,
			},
			{
				uiId: 'v2_high',
				name: 'High',
				color: '#ef4444',
				min: 80,
				max: 100,
				noAlert: false,
				deadlineDays: 3,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: true,
			},
		],
	},
	{
		id: 'v1',
		version: 'v1',
		createdAt: new Date(Date.now() - 86400000 * 45).toISOString(),
		createdBy: 'Sam Rivera',
		tierCount: 2,
		tiers: [
			{
				uiId: 'v1_low',
				name: 'Low',
				color: '#22c55e',
				min: 0,
				max: 60,
				noAlert: true,
				deadlineDays: 0,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: false,
			},
			{
				uiId: 'v1_high',
				name: 'High',
				color: '#ef4444',
				min: 60,
				max: 100,
				noAlert: false,
				deadlineDays: 3,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: false,
			},
		],
	},
];

function formatSla(t: FormTier): string {
	if (t.noAlert) return '—';
	const parts: string[] = [];
	if (t.deadlineDays > 0) parts.push(`${t.deadlineDays}d`);
	if (t.deadlineHours > 0) parts.push(`${t.deadlineHours}h`);
	if (t.deadlineMinutes > 0) parts.push(`${t.deadlineMinutes}m`);
	return parts.length > 0 ? parts.join(' ') : '—';
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

function rebalance(tiers: FormTier[]): FormTier[] {
	if (tiers.length === 0) return tiers;
	const out = [...tiers];
	out[0] = { ...out[0], min: 0 };
	for (let i = 1; i < out.length; i++) {
		out[i] = { ...out[i], min: out[i - 1].max };
	}
	out[out.length - 1] = { ...out[out.length - 1], max: 100 };
	return out;
}

interface ThresholdTableProps {
	tiers: FormTier[];
	editMode: boolean;
	onTierChange?: (index: number, updated: FormTier) => void;
	onAddTier?: () => void;
	onDeleteTier?: (index: number) => void;
}

function ThresholdTable({
	tiers,
	editMode,
	onTierChange,
	onAddTier,
	onDeleteTier,
}: ThresholdTableProps) {
	const cell = 'px-3 py-3 text-sm text-[#0a0a0a] text-start align-middle';
	const head = 'h-10 px-3 py-2 text-sm font-medium text-[#0a0a0a] text-start';

	return (
		<div className="flex flex-col gap-3">
			<div className="overflow-hidden rounded-md border border-[#e5e5e5] bg-white overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow className="border-b border-[#e5e5e5] bg-[#f5f5f5] hover:bg-[#f5f5f5]">
							<TableHead className={`${head} min-w-[140px]`}>Name</TableHead>
							<TableHead className={`${head} min-w-[120px]`}>Color</TableHead>
							<TableHead className={`${head} min-w-[90px]`}>Min %</TableHead>
							<TableHead className={`${head} min-w-[90px]`}>Max %</TableHead>
							<TableHead className={`${head} min-w-[90px]`}>No Alert</TableHead>
							<TableHead className={`${head} min-w-[200px]`}>
								SLA Deadline
							</TableHead>
							<TableHead className={`${head} min-w-[120px]`}>
								Auto Hibernate
							</TableHead>
							{editMode && <TableHead className={`${head} min-w-[60px]`} />}
						</TableRow>
					</TableHeader>
					<TableBody>
						{tiers.map((tier, i) => {
							const isFirst = i === 0;
							const isLast = i === tiers.length - 1;
							const nonDeletable = i === 0 || i === 1;
							return (
								<TableRow
									key={tier.uiId}
									className="h-[52px] border-b border-[#e5e5e5] last:border-b-0"
								>
									<TableCell className={cell}>
										{editMode ? (
											<Input
												value={tier.name}
												placeholder="Tier name"
												className="h-8 text-sm"
												onChange={(e) =>
													onTierChange?.(i, { ...tier, name: e.target.value })
												}
											/>
										) : (
											<span className="font-medium">{tier.name}</span>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<div className="flex items-center gap-2">
												<input
													type="color"
													value={tier.color}
													className="h-8 w-8 cursor-pointer rounded border border-[#e5e5e5] p-0.5"
													onChange={(e) =>
														onTierChange?.(i, {
															...tier,
															color: e.target.value,
														})
													}
												/>
												<span className="text-xs uppercase text-[#737373]">
													{tier.color}
												</span>
											</div>
										) : (
											<div className="flex items-center gap-2">
												<span
													className="inline-block h-5 w-5 rounded-full border border-[#e5e5e5]"
													style={{ backgroundColor: tier.color }}
												/>
												<span className="text-xs uppercase text-[#737373]">
													{tier.color}
												</span>
											</div>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<div className="flex items-center gap-1">
												<Input
													type="number"
													value={tier.min}
													disabled={isFirst}
													className={cn(
														'h-8 w-[70px] text-sm',
														isFirst && 'bg-[#f5f5f5]',
													)}
													onChange={(e) =>
														onTierChange?.(i, {
															...tier,
															min: Math.max(
																0,
																Math.min(100, Number(e.target.value)),
															),
														})
													}
												/>
												<span className="text-xs text-[#737373]">%</span>
											</div>
										) : (
											<span>{tier.min}%</span>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<div className="flex items-center gap-1">
												<Input
													type="number"
													value={tier.max}
													disabled={isLast}
													className={cn(
														'h-8 w-[70px] text-sm',
														isLast && 'bg-[#f5f5f5]',
													)}
													onChange={(e) =>
														onTierChange?.(i, {
															...tier,
															max: Math.max(
																0,
																Math.min(100, Number(e.target.value)),
															),
														})
													}
												/>
												<span className="text-xs text-[#737373]">%</span>
											</div>
										) : (
											<span>{tier.max}%</span>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<Switch
												checked={tier.noAlert}
												onCheckedChange={(checked) =>
													onTierChange?.(i, {
														...tier,
														noAlert: checked,
														...(checked
															? {
																	deadlineDays: 0,
																	deadlineHours: 0,
																	deadlineMinutes: 0,
																	autoHibernate: false,
																}
															: {}),
													})
												}
											/>
										) : (
											<span
												className={cn(
													'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
													tier.noAlert
														? 'bg-green-50 text-green-700'
														: 'bg-gray-50 text-[#646464]',
												)}
											>
												{tier.noAlert ? 'Yes' : 'No'}
											</span>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<div className="flex items-center gap-1">
												{(
													[
														'deadlineDays',
														'deadlineHours',
														'deadlineMinutes',
													] as const
												).map((field, fi) => (
													<div
														key={field}
														className="flex flex-col items-center"
													>
														<Input
															type="number"
															value={tier[field]}
															min={0}
															max={fi === 1 ? 23 : fi === 2 ? 59 : 999}
															disabled={tier.noAlert}
															className={cn(
																'h-8 w-[55px] text-center text-sm',
																tier.noAlert && 'bg-[#f5f5f5]',
															)}
															onChange={(e) =>
																onTierChange?.(i, {
																	...tier,
																	[field]: Math.max(0, Number(e.target.value)),
																})
															}
														/>
														<span className="text-[10px] text-[#737373]">
															{['DD', 'HH', 'MM'][fi]}
														</span>
													</div>
												))}
											</div>
										) : (
											<span>{formatSla(tier)}</span>
										)}
									</TableCell>
									<TableCell className={cell}>
										{editMode ? (
											<Switch
												checked={tier.autoHibernate}
												disabled={tier.noAlert}
												onCheckedChange={(checked) =>
													onTierChange?.(i, { ...tier, autoHibernate: checked })
												}
											/>
										) : (
											<span
												className={cn(
													'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
													tier.autoHibernate
														? 'bg-green-50 text-green-700'
														: 'bg-gray-50 text-[#646464]',
												)}
											>
												{tier.noAlert ? '—' : tier.autoHibernate ? 'Yes' : 'No'}
											</span>
										)}
									</TableCell>
									{editMode && (
										<TableCell className={cell}>
											{!nonDeletable && (
												<button
													type="button"
													disabled={tiers.length <= 1}
													onClick={() => onDeleteTier?.(i)}
													className="rounded p-1 text-[#737373] hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											)}
										</TableCell>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
			{editMode && (
				<button
					type="button"
					onClick={onAddTier}
					className="flex items-center gap-1.5 self-start px-1 text-sm font-medium text-[#0a0a0a] hover:text-[#171717]"
				>
					<Plus className="h-4 w-4" />
					Add More Threshold
				</button>
			)}
		</div>
	);
}

export default function ThresholdConfigPage() {
	const [tiers, setTiers] = useState<FormTier[]>(DEFAULT_TIERS);
	const [snapshot, setSnapshot] = useState<FormTier[]>(DEFAULT_TIERS);
	const [editMode, setEditMode] = useState(false);
	const [history] = useState<HistoryVersion[]>(MOCK_HISTORY);
	const [sheetOpen, setSheetOpen] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<HistoryVersion | null>(
		null,
	);

	const activeVersion = useMemo(() => history[0], [history]);

	const handleTierChange = useCallback((index: number, updated: FormTier) => {
		setTiers((prev) => {
			const next = [...prev];
			const old = prev[index];
			next[index] = updated;
			if (index < next.length - 1 && updated.max !== old.max) {
				next[index + 1] = { ...next[index + 1], min: updated.max };
			}
			if (index > 0 && updated.min !== old.min) {
				next[index - 1] = { ...next[index - 1], max: updated.min };
			}
			return rebalance(next);
		});
	}, []);

	const handleAddTier = useCallback(() => {
		setTiers((prev) => {
			const lastMax = prev.length > 0 ? prev[prev.length - 1].max : 0;
			const newTier: FormTier = {
				uiId: `tier_${Date.now()}`,
				name: '',
				color: '#6366f1',
				min: lastMax,
				max: 100,
				noAlert: false,
				deadlineDays: 3,
				deadlineHours: 0,
				deadlineMinutes: 0,
				autoHibernate: false,
			};
			return rebalance([...prev, newTier]);
		});
	}, []);

	const handleDeleteTier = useCallback((index: number) => {
		setTiers((prev) => rebalance(prev.filter((_, i) => i !== index)));
	}, []);

	const handleEnterEdit = () => {
		setSnapshot(tiers);
		setEditMode(true);
	};

	const handleCancel = () => {
		setTiers(snapshot);
		setEditMode(false);
	};

	const handleSave = () => {
		setEditMode(false);
		toast.success('Configuration saved successfully');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<h1 className="text-xl font-semibold text-[#0A0A0A]">
					Alert Configuration
				</h1>
				<p className="mt-0.5 text-sm text-[#737373]">
					Manage screening alert thresholds and SLA settings
				</p>
			</div>

			<div className="p-6">
				<Tabs defaultValue="active">
					<TabsList>
						<TabsTrigger value="active">Active Alert Configuration</TabsTrigger>
						<TabsTrigger value="history">
							Alert Configuration History
						</TabsTrigger>
					</TabsList>

					<TabsContent value="active" className="mt-6">
						<div className="overflow-hidden rounded-xl bg-white shadow-sm">
							<div className="flex items-start justify-between px-6 py-4">
								<div className="flex flex-col gap-1">
									<h3 className="text-base font-semibold text-[#0a0a0a]">
										Active Alert Configuration
									</h3>
									<div className="flex flex-wrap items-center gap-4 text-xs text-[#737373]">
										<span>Version: {activeVersion.version}</span>
										<span>
											Created At: {formatDate(activeVersion.createdAt)}
										</span>
										<span>Created By: {activeVersion.createdBy}</span>
									</div>
								</div>
								<div className="flex items-center gap-2">
									{editMode ? (
										<>
											<Button
												variant="outline"
												size="sm"
												onClick={handleCancel}
											>
												<X className="mr-1 h-4 w-4" />
												Cancel
											</Button>
											<Button
												size="sm"
												className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
												onClick={handleSave}
											>
												Save
											</Button>
										</>
									) : (
										<Button
											variant="outline"
											size="sm"
											onClick={handleEnterEdit}
										>
											<Pencil className="mr-1 h-4 w-4" />
											Edit
										</Button>
									)}
								</div>
							</div>
							<div className="px-6 pb-6">
								<ThresholdTable
									tiers={tiers}
									editMode={editMode}
									onTierChange={handleTierChange}
									onAddTier={handleAddTier}
									onDeleteTier={handleDeleteTier}
								/>
							</div>
						</div>
					</TabsContent>

					<TabsContent value="history" className="mt-6">
						<div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-white">
							<Table>
								<TableHeader>
									<TableRow className="bg-[#FAFAFA]">
										<TableHead className="text-xs font-semibold text-[#737373]">
											Version
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Created At
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Created By
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Tiers
										</TableHead>
										<TableHead className="text-xs font-semibold text-[#737373]">
											Status
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{history.map((v, i) => (
										<TableRow
											key={v.id}
											className="cursor-pointer"
											onClick={() => {
												setSelectedVersion(v);
												setSheetOpen(true);
											}}
										>
											<TableCell className="text-sm font-medium text-[#0a0a0a]">
												{v.version}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{formatDate(v.createdAt)}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{v.createdBy}
											</TableCell>
											<TableCell className="text-sm text-[#525252]">
												{v.tierCount} tiers
											</TableCell>
											<TableCell>
												{i === 0 ? (
													<Badge className="bg-green-50 text-green-700 hover:bg-green-50 border border-green-200 text-xs">
														Active
													</Badge>
												) : (
													<Badge variant="secondary" className="text-xs">
														Archived
													</Badge>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</TabsContent>
				</Tabs>
			</div>

			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetContent className="w-[700px] sm:max-w-[700px] overflow-y-auto">
					<SheetHeader>
						<SheetTitle>Configuration {selectedVersion?.version}</SheetTitle>
					</SheetHeader>
					{selectedVersion && (
						<div className="mt-4 flex flex-col gap-4">
							<div className="flex flex-wrap gap-4 text-xs text-[#737373]">
								<span>Created At: {formatDate(selectedVersion.createdAt)}</span>
								<span>Created By: {selectedVersion.createdBy}</span>
							</div>
							<ThresholdTable tiers={selectedVersion.tiers} editMode={false} />
						</div>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
